// Environment mode types
export enum EnvMode {
  LOCAL = 'local',
  STAGING = 'staging',
  PRODUCTION = 'production',
}

// Subscription tier structure
export interface SubscriptionTierData {
  priceId: string;
  name: string;
}

// Subscription tiers structure
export interface SubscriptionTiers {
  INDIVIDUAL: SubscriptionTierData;
  STARTER: SubscriptionTierData;
  PROFESSIONAL: SubscriptionTierData;
  ENTERPRISE: SubscriptionTierData;
  // Yearly plans with 15% discount
  INDIVIDUAL_YEARLY: SubscriptionTierData;
  STARTER_YEARLY: SubscriptionTierData;
  PROFESSIONAL_YEARLY: SubscriptionTierData;
  ENTERPRISE_YEARLY: SubscriptionTierData;
}

// Configuration object
interface Config {
  ENV_MODE: EnvMode;
  IS_LOCAL: boolean;
  IS_STAGING: boolean;
  SUBSCRIPTION_TIERS: SubscriptionTiers;
  ENABLE_REACT_QUERY_DEVTOOLS: boolean;
}

// Production tier IDs (EUR pricing)
const PROD_TIERS: SubscriptionTiers = {
  INDIVIDUAL: {
    priceId: 'price_1SI3MCA8VCvuELr5nQMqvzCA',
    name: 'Individual €49',
  },
  STARTER: {
    priceId: 'price_1SI3MEA8VCvuELr5OyMthF5q',
    name: 'Starter €299',
  },
  PROFESSIONAL: {
    priceId: 'price_1SI3MGA8VCvuELr57d29lZlE',
    name: 'Professional €899',
  },
  ENTERPRISE: {
    priceId: 'price_1SI3MIA8VCvuELr5JtyeJO8U',
    name: 'Enterprise €2,499',
  },
  // Yearly plans with 15% discount
  INDIVIDUAL_YEARLY: {
    priceId: 'price_1SI3MDA8VCvuELr5bujVfISg',
    name: 'Individual €500/year',
  },
  STARTER_YEARLY: {
    priceId: 'price_1SI3MFA8VCvuELr5jPTEQYoE',
    name: 'Starter €3,060/year',
  },
  PROFESSIONAL_YEARLY: {
    priceId: 'price_1SI3MHA8VCvuELr5UlL9wAVg',
    name: 'Professional €9,180/year',
  },
  ENTERPRISE_YEARLY: {
    priceId: 'price_1SI3MJA8VCvuELr5K30oNv08',
    name: 'Enterprise €25,490/year',
  },
} as const;

// Staging tier IDs (EUR pricing)
const STAGING_TIERS: SubscriptionTiers = {
  INDIVIDUAL: {
    priceId: 'price_TODO_INDIVIDUAL_49_EUR_STAGING',
    name: 'Individual €49',
  },
  STARTER: {
    priceId: 'price_TODO_STARTER_299_EUR_STAGING',
    name: 'Starter €299',
  },
  PROFESSIONAL: {
    priceId: 'price_TODO_PROFESSIONAL_899_EUR_STAGING',
    name: 'Professional €899',
  },
  ENTERPRISE: {
    priceId: 'price_TODO_ENTERPRISE_2499_EUR_STAGING',
    name: 'Enterprise €2,499',
  },
  // Yearly plans with 15% discount
  INDIVIDUAL_YEARLY: {
    priceId: 'price_TODO_INDIVIDUAL_49_YEARLY_EUR_STAGING',
    name: 'Individual €500/year',
  },
  STARTER_YEARLY: {
    priceId: 'price_TODO_STARTER_299_YEARLY_EUR_STAGING',
    name: 'Starter €3,060/year',
  },
  PROFESSIONAL_YEARLY: {
    priceId: 'price_TODO_PROFESSIONAL_899_YEARLY_EUR_STAGING',
    name: 'Professional €9,180/year',
  },
  ENTERPRISE_YEARLY: {
    priceId: 'price_TODO_ENTERPRISE_2499_YEARLY_EUR_STAGING',
    name: 'Enterprise €25,490/year',
  },
} as const;

function getEnvironmentMode(): EnvMode {
  const envMode = process.env.NEXT_PUBLIC_ENV_MODE.toUpperCase();
  switch (envMode) {
    case 'LOCAL':
      return EnvMode.LOCAL;
    case 'STAGING':
      return EnvMode.STAGING;
    case 'PRODUCTION':
      return EnvMode.PRODUCTION;
  //   default:
  //     if (process.env.NODE_ENV === 'development') {
  //       return EnvMode.LOCAL;
  //     } else {
  //       return EnvMode.PRODUCTION;
  //     }
  }
}

const currentEnvMode = getEnvironmentMode();

export const config: Config = {
  ENV_MODE: currentEnvMode,
  IS_LOCAL: currentEnvMode === EnvMode.LOCAL,
  IS_STAGING: currentEnvMode === EnvMode.STAGING,
  SUBSCRIPTION_TIERS:
    currentEnvMode === EnvMode.STAGING ? STAGING_TIERS : PROD_TIERS,
  ENABLE_REACT_QUERY_DEVTOOLS: process.env.NEXT_PUBLIC_ENABLE_REACT_QUERY_DEVTOOLS === 'true',
};

export const isLocalMode = (): boolean => {
  return config.IS_LOCAL;
};

export const isStagingMode = (): boolean => {
  return config.IS_STAGING;
};

export const isReactQueryDevtoolsEnabled = (): boolean => {
  return config.ENABLE_REACT_QUERY_DEVTOOLS;
};


// Plan type identification functions
export const isMonthlyPlan = (priceId: string): boolean => {
  const allTiers = config.SUBSCRIPTION_TIERS;
  const monthlyTiers = [
    allTiers.INDIVIDUAL, allTiers.STARTER, allTiers.PROFESSIONAL, allTiers.ENTERPRISE
  ];
  return monthlyTiers.some(tier => tier.priceId === priceId);
};

export const isYearlyPlan = (priceId: string): boolean => {
  const allTiers = config.SUBSCRIPTION_TIERS;
  const yearlyTiers = [
    allTiers.INDIVIDUAL_YEARLY, allTiers.STARTER_YEARLY, 
    allTiers.PROFESSIONAL_YEARLY, allTiers.ENTERPRISE_YEARLY
  ];
  return yearlyTiers.some(tier => tier.priceId === priceId);
};

// Legacy support for yearly commitment (no longer used in new pricing)
export const isYearlyCommitmentPlan = (priceId: string): boolean => {
  return false;
};

export const getYearlyCommitmentTier = (priceId: string): number => {
  return 0;
};

export const isYearlyCommitmentDowngrade = (currentPriceId: string, newPriceId: string): boolean => {
  return false;
};

// Tier level mappings for all plan types (EUR pricing)
const PLAN_TIERS = {
  // Production monthly plans
  [PROD_TIERS.INDIVIDUAL.priceId]: { tier: 1, type: 'monthly', name: 'Individual €49' },
  [PROD_TIERS.STARTER.priceId]: { tier: 2, type: 'monthly', name: 'Starter €299' },
  [PROD_TIERS.PROFESSIONAL.priceId]: { tier: 3, type: 'monthly', name: 'Professional €899' },
  [PROD_TIERS.ENTERPRISE.priceId]: { tier: 4, type: 'monthly', name: 'Enterprise €2,499' },
  
  // Production yearly plans  
  [PROD_TIERS.INDIVIDUAL_YEARLY.priceId]: { tier: 1, type: 'yearly', name: 'Individual €500/year' },
  [PROD_TIERS.STARTER_YEARLY.priceId]: { tier: 2, type: 'yearly', name: 'Starter €3,060/year' },
  [PROD_TIERS.PROFESSIONAL_YEARLY.priceId]: { tier: 3, type: 'yearly', name: 'Professional €9,180/year' },
  [PROD_TIERS.ENTERPRISE_YEARLY.priceId]: { tier: 4, type: 'yearly', name: 'Enterprise €25,490/year' },

  // Staging monthly plans
  [STAGING_TIERS.INDIVIDUAL.priceId]: { tier: 1, type: 'monthly', name: 'Individual €49' },
  [STAGING_TIERS.STARTER.priceId]: { tier: 2, type: 'monthly', name: 'Starter €299' },
  [STAGING_TIERS.PROFESSIONAL.priceId]: { tier: 3, type: 'monthly', name: 'Professional €899' },
  [STAGING_TIERS.ENTERPRISE.priceId]: { tier: 4, type: 'monthly', name: 'Enterprise €2,499' },
  
  // Staging yearly plans
  [STAGING_TIERS.INDIVIDUAL_YEARLY.priceId]: { tier: 1, type: 'yearly', name: 'Individual €500/year' },
  [STAGING_TIERS.STARTER_YEARLY.priceId]: { tier: 2, type: 'yearly', name: 'Starter €3,060/year' },
  [STAGING_TIERS.PROFESSIONAL_YEARLY.priceId]: { tier: 3, type: 'yearly', name: 'Professional €9,180/year' },
  [STAGING_TIERS.ENTERPRISE_YEARLY.priceId]: { tier: 4, type: 'yearly', name: 'Enterprise €25,490/year' },
} as const;

export const getPlanInfo = (priceId: string) => {
  return PLAN_TIERS[priceId as keyof typeof PLAN_TIERS] || { tier: 0, type: 'unknown', name: 'Unknown' };
};

// Plan change validation function
export const isPlanChangeAllowed = (currentPriceId: string, newPriceId: string): { allowed: boolean; reason?: string } => {
  const currentPlan = getPlanInfo(currentPriceId);
  const newPlan = getPlanInfo(newPriceId);

  // Allow if same plan
  if (currentPriceId === newPriceId) {
    return { allowed: true };
  }

  // Restriction 1: Don't allow downgrade from monthly to lower monthly
  if (currentPlan.type === 'monthly' && newPlan.type === 'monthly' && newPlan.tier < currentPlan.tier) {
    return { 
      allowed: false, 
      reason: 'Downgrading to a lower monthly plan is not allowed. You can only upgrade to a higher tier or switch to yearly billing.' 
    };
  }

  // Restriction 2: Don't allow downgrade from yearly to lower yearly
  if (currentPlan.type === 'yearly' && newPlan.type === 'yearly' && newPlan.tier < currentPlan.tier) {
    return { 
      allowed: false, 
      reason: 'Downgrading to a lower yearly plan is not allowed. You can only upgrade to higher tiers.' 
    };
  }

  // Restriction 3: Don't allow downgrade from yearly to monthly
  if (currentPlan.type === 'yearly' && newPlan.type === 'monthly') {
    return { 
      allowed: false, 
      reason: 'Switching from yearly to monthly billing is not allowed. Please contact support for assistance.' 
    };
  }

  // Allow all other changes (upgrades, monthly to yearly at same or higher tier)
  return { allowed: true };
};

// Export subscription tier type for typing elsewhere
export type SubscriptionTier = keyof typeof PROD_TIERS;
